import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, Button, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency } from '../../shared/utils/format';
import {
  useFunnel,
  useOpportunities,
  type Funnel,
  type Opportunity,
} from './useOpportunities';

/* ── Porte de `erpone_funil_de_vendas_analytics` ──────────────────────────
   Layout 1:1 com o protótipo (header + Pipeline Conversion + 3 KPIs +
   Forecast vs Actual + Stale Deals), mas cada número vem do agregado
   `GET /crm/funnel` (RF-007). Nada é estático: os deltas comparam com a
   janela anterior equivalente, calculada da listagem de oportunidades. */

type Period = 30 | 90 | 0;

const PERIOD_LABEL: Record<Period, string> = {
  30: 'Last 30 Days',
  90: 'Last 90 Days',
  0: 'Tudo',
};

const STAGE_STYLE: Record<string, { bg: string; text: string }> = {
  PROSPECTING: { bg: 'bg-primary-container', text: 'text-on-primary-container' },
  QUALIFICATION: { bg: 'bg-secondary-container', text: 'text-on-secondary-container' },
  PROPOSAL: { bg: 'bg-[#adc7f7]', text: 'text-primary' },
  NEGOTIATION: { bg: 'bg-[#d3e4fe]', text: 'text-primary' },
  CLOSING: { bg: 'bg-[#e6e8ea]', text: 'text-primary' },
};

const STAGE_CLIP = [
  'polygon(0 0, 100% 0, 95% 100%, 5% 100%)',
  'polygon(0 0, 100% 0, 93% 100%, 7% 100%)',
  'polygon(0 0, 100% 0, 90% 100%, 10% 100%)',
  'polygon(0 0, 100% 0, 85% 100%, 15% 100%)',
  'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
];

/* Larguras fixas do protótipo, coordenadas com os clip-paths acima para
   formar a silhueta contínua do funil. São decorativas (no protótipo
   4020/12450 = 32% também renderiza a 90%) — a informação real está no
   texto de cada estágio (contagem · valor · conversão). Largura dinâmica
   por contagem quebra as inclinações e o trapézio vaza do card. */
const STAGE_WIDTH = ['w-full', 'w-[90%]', 'w-[76%]', 'w-[60%]', 'w-[42%]'];

function inWindow(o: Opportunity, from: number, to: number): boolean {
  const t = new Date(o.createdAt).getTime();
  return t >= from && t < to;
}

function kpisOf(list: Opportunity[]): {
  conversion: number;
  cycle: number | null;
  weighted: number;
} {
  const total = list.length;
  const won = list.filter((o) => o.stage === 'CLOSING' && o.outcome === 'WON');
  const conversion = total === 0 ? 0 : (won.length / total) * 100;
  const cycles = won.map((o) =>
    Math.max(
      0,
      Math.round(
        (new Date(o.updatedAt).getTime() - new Date(o.createdAt).getTime()) / 86_400_000,
      ),
    ),
  );
  const cycle =
    cycles.length === 0 ? null : cycles.reduce((a, b) => a + b, 0) / cycles.length;
  const weighted = list
    .filter((o) => o.stage !== 'CLOSING')
    .reduce((sum, o) => sum + (o.amount * o.probability) / 100, 0);
  return { conversion, cycle, weighted };
}

function exportCsv(funnel: Funnel): void {
  const lines = [
    'estagio;contagem;valor;conversao_estagio_anterior_pct',
    ...funnel.stages.map((s) =>
      [s.label, s.count, s.value, s.conversionFromPrevious ?? ''].join(';'),
    ),
    '',
    'oportunidade;conta;estagio;valor;dias_parado',
    ...funnel.staleDeals.map((d) =>
      [d.title, d.customerName ?? '', d.stage, d.value, d.daysIdle].join(';'),
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'funil-vendas.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function FunnelPage(): JSX.Element {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>(30);
  const { data: funnel, isLoading, error } = useFunnel(period);
  const opportunitiesQuery = useOpportunities({ page: 1, limit: 100 });

  const deltas = useMemo(() => {
    const list = opportunitiesQuery.data?.data ?? [];
    if (period === 0 || list.length === 0) return null;
    const now = Date.now();
    const curr = list.filter((o) => inWindow(o, now - period * 86_400_000, now));
    const prev = list.filter((o) =>
      inWindow(o, now - 2 * period * 86_400_000, now - period * 86_400_000),
    );
    if (prev.length === 0) return null;
    const c = kpisOf(curr);
    const p = kpisOf(prev);
    return {
      conversion: c.conversion - p.conversion,
      cycle:
        c.cycle === null || p.cycle === null ? null : p.cycle - c.cycle,
      weighted:
        p.weighted === 0 ? null : ((c.weighted - p.weighted) / p.weighted) * 100,
    };
  }, [opportunitiesQuery.data, period]);

  const cyclePeriod = (days: number) => {
    setPeriod(days as Period);
  };

  return (
    <main className="flex-1 overflow-y-auto p-gutter grid grid-cols-12 gap-gutter">
      {/* Page Header */}
      <div className="col-span-12 flex justify-between items-end mb-sm">
        <div>
          <h2 className="font-headline-md text-headline-md text-primary">Funil de Vendas</h2>
          <p className="font-body-md text-body-md text-secondary">
            Analytics &amp; Conversion Rates
          </p>
        </div>
        <div className="flex gap-sm">
          <button
            type="button"
            onClick={() => cyclePeriod(period === 30 ? 90 : period === 90 ? 0 : 30)}
            className="bg-surface-container border border-outline-variant px-md py-sm rounded-lg font-label-md flex items-center gap-xs text-on-surface"
            title="Alternar período: 30 → 90 dias → tudo"
          >
            <span className="material-symbols-outlined text-[18px]">calendar_month</span>
            {PERIOD_LABEL[period]}
          </button>
          <Button
            variant="primary"
            onClick={() => funnel && exportCsv(funnel)}
            disabled={!funnel}
          >
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
              download
            </span>
            Export Report
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="col-span-12">
          <LoadingSkeleton count={4} height="72px" />
        </div>
      ) : error || !funnel ? (
        <div className="col-span-12">
          <p className="font-body-md text-body-md text-error">
            Erro ao carregar o funil: {(error as Error)?.message ?? 'desconhecido'}
          </p>
        </div>
      ) : funnel.stages.every((s) => s.count === 0) ? (
        <div className="col-span-12">
          <Card>
            <p className="m-0 font-body-md text-body-md text-on-surface-variant">
              Nenhuma oportunidade para montar o funil. Crie uma oportunidade para começar.
            </p>
          </Card>
        </div>
      ) : (
        <>
          {/* Funnel Graphic (Left) */}
          <div className="col-span-12 lg:col-span-7 bg-surface-container-lowest border border-outline-variant rounded-xl p-lg flex flex-col">
            <h3 className="font-headline-sm text-headline-sm text-on-surface mb-md">
              Pipeline Conversion
            </h3>
            <div className="flex-1 flex flex-col justify-center items-center py-xl w-full max-w-lg mx-auto">
              {funnel.stages.map((stage, index) => {
                const style = STAGE_STYLE[stage.stage] ?? STAGE_STYLE['PROSPECTING']!;
                const first = index === 0 ? 'rounded-t-lg' : '';
                const last = index === funnel.stages.length - 1 ? 'rounded-b-lg' : '';
                return (
                  <div key={stage.stage} className={`${STAGE_WIDTH[index]} relative mb-sm group`}>
                    <div
                      className={`${style.bg} min-h-16 flex flex-col items-center justify-center px-md py-xs ${first} ${last}`}
                      style={{ clipPath: STAGE_CLIP[index] }}
                    >
                      <span className={`font-label-md ${style.text}`}>{stage.label}</span>
                      <span className={`font-body-md ${style.text} flex flex-wrap items-center justify-center gap-xs text-center leading-snug`}>
                        <span>
                          {stage.count} · {formatCurrency(stage.value)}
                        </span>
                        {stage.conversionFromPrevious !== null && (
                          <span className="text-xs opacity-75">
                            · conv. {stage.conversionFromPrevious}%
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* KPIs (Right) */}
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-sm">
            <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-md flex items-center gap-md">
              <div className="w-12 h-12 rounded-full bg-primary-container text-on-primary-container flex items-center justify-center">
                <span className="material-symbols-outlined">monitoring</span>
              </div>
              <div>
                <p className="font-label-md text-secondary">Overall Conversion Rate</p>
                <div className="flex items-baseline gap-xs">
                  <h4 className="font-display-lg text-display-lg text-on-surface">
                    {funnel.overallConversion.toFixed(1)}%
                  </h4>
                  {deltas && (
                    <span
                      className={`text-[12px] flex items-center ${
                        deltas.conversion >= 0 ? 'text-[#2e7d32]' : 'text-error'
                      }`}
                    >
                      <span className="material-symbols-outlined text-[14px]">
                        {deltas.conversion >= 0 ? 'arrow_upward' : 'arrow_downward'}
                      </span>
                      {Math.abs(deltas.conversion).toFixed(1)}pp
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-md flex items-center gap-md">
              <div className="w-12 h-12 rounded-full bg-secondary-container text-on-secondary-container flex items-center justify-center">
                <span className="material-symbols-outlined">timer</span>
              </div>
              <div>
                <p className="font-label-md text-secondary">Average Cycle Time</p>
                <div className="flex items-baseline gap-xs">
                  <h4 className="font-display-lg text-display-lg text-on-surface">
                    {funnel.averageCycleDays === null
                      ? '—'
                      : `${funnel.averageCycleDays} Days`}
                  </h4>
                  {deltas?.cycle !== null && deltas?.cycle !== undefined && (
                    <span className="text-[12px] text-[#2e7d32] flex items-center">
                      <span className="material-symbols-outlined text-[14px]">
                        arrow_downward
                      </span>
                      {Math.abs(Math.round(deltas.cycle))} days
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-md flex items-center gap-md">
              <div className="w-12 h-12 rounded-full bg-[#f2bc82] text-on-tertiary-fixed flex items-center justify-center">
                <span className="material-symbols-outlined">monetization_on</span>
              </div>
              <div>
                <p className="font-label-md text-secondary">Weighted Pipeline Value</p>
                <div className="flex items-baseline gap-xs">
                  <h4 className="font-display-lg text-display-lg text-on-surface">
                    {formatCurrency(funnel.weightedValue)}
                  </h4>
                  {deltas?.weighted !== null && deltas?.weighted !== undefined && (
                    <span className="text-[12px] text-[#2e7d32] flex items-center">
                      <span className="material-symbols-outlined text-[14px]">
                        arrow_upward
                      </span>
                      {Math.abs(Math.round(deltas.weighted))}%
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Forecast vs Actual */}
            <div className="flex-1 bg-surface-container-lowest border border-outline-variant rounded-xl p-md flex flex-col justify-between">
              <p className="font-label-md text-secondary mb-xs">
                Forecast vs Actual (ganho real × pipeline ponderado)
              </p>
              <div className="flex-1 bg-surface-container-low rounded flex items-end p-xs gap-xs relative overflow-hidden min-h-24">
                {funnel.monthly.map((m) => {
                  const max = Math.max(
                    1,
                    ...funnel.monthly.flatMap((x) => [x.won, x.forecast]),
                  );
                  return (
                    <div
                      key={m.month}
                      className="flex-1 flex items-end gap-[2px] h-full"
                      title={`${m.month}: ganho ${formatCurrency(m.won)} · forecast ${formatCurrency(m.forecast)}`}
                    >
                      <div
                        className="w-1/2 bg-primary-container rounded-t opacity-50"
                        style={{ height: `${Math.max(4, (m.forecast / max) * 100)}%` }}
                      />
                      <div
                        className="w-1/2 bg-primary rounded-t"
                        style={{ height: `${Math.max(4, (m.won / max) * 100)}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="mt-xs font-label-sm text-label-sm text-secondary">
                {funnel.monthly[0]?.month} – {funnel.monthly[funnel.monthly.length - 1]?.month}
              </p>
            </div>
          </div>

          {/* Stale Deals Table (Bottom) */}
          <div className="col-span-12 bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden mt-sm">
            <div className="p-md border-b border-outline-variant flex justify-between items-center bg-surface-container-low">
              <h3 className="font-headline-sm text-headline-sm text-on-surface flex items-center gap-xs">
                <span className="material-symbols-outlined text-error">warning</span>
                Stale Deals Action Required
              </h3>
              <button
                type="button"
                className="font-label-md text-primary hover:underline"
                onClick={() => void navigate({ to: '/crm/pipeline' })}
              >
                View All
              </button>
            </div>
            {funnel.staleDeals.length === 0 ? (
              <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
                Nenhum deal parado — pipeline em movimento.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-outline-variant bg-surface">
                      <th className="p-sm font-label-md text-secondary whitespace-nowrap">
                        Opportunity Name
                      </th>
                      <th className="p-sm font-label-md text-secondary whitespace-nowrap">
                        Account
                      </th>
                      <th className="p-sm font-label-md text-secondary whitespace-nowrap">
                        Stage
                      </th>
                      <th className="p-sm font-label-md text-secondary whitespace-nowrap">
                        Value
                      </th>
                      <th className="p-sm font-label-md text-secondary whitespace-nowrap">
                        Days Idle
                      </th>
                      <th className="p-sm font-label-md text-secondary text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="font-body-sm">
                    {funnel.staleDeals.map((deal) => {
                      const style =
                        STAGE_STYLE[deal.stage] ?? STAGE_STYLE['PROSPECTING']!;
                      return (
                        <tr
                          key={deal.id}
                          className="border-b border-surface-container hover:bg-surface-container-low transition-colors cursor-pointer"
                          onClick={() => void navigate({ to: '/crm/pipeline' })}
                        >
                          <td className="p-sm font-medium text-on-surface">{deal.title}</td>
                          <td className="p-sm text-secondary">
                            {deal.customerName ?? '—'}
                          </td>
                          <td className="p-sm">
                            <span
                              className={`${style.bg} ${style.text} px-xs py-[2px] rounded text-[10px]`}
                            >
                              {deal.stage}
                            </span>
                          </td>
                          <td className="p-sm">{formatCurrency(deal.value)}</td>
                          <td className="p-sm text-error font-medium">{deal.daysIdle} Days</td>
                          <td className="p-sm text-right">
                            <button
                              type="button"
                              className="material-symbols-outlined text-secondary hover:text-primary"
                              aria-label={`Abrir ${deal.title}`}
                            >
                              arrow_forward_ios
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </main>
  );
}
